import { styled } from '@mui/system'

// Main Sidebar Container
export const SidebarContainer = styled('div')({
  position: 'fixed',
  right: '20px',
  bottom: '20px',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '12px',
  
  '@media (max-width: 768px)': {
    right: '15px',
    bottom: '15px',
  }
})

// Expandable Sidebar
export const Sidebar = styled('div')(({ isExpanded }) => ({
  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)',
  backdropFilter: 'blur(20px)',
  borderRadius: '28px',
  padding: '8px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 16px 32px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  width: isExpanded ? '280px' : '56px',
  height: isExpanded ? 'auto' : '56px',
  overflow: 'hidden',
  
  '@media (max-width: 768px)': {
    width: isExpanded ? '320px' : '52px',
    height: isExpanded ? 'auto' : '52px',
    borderRadius: '26px',
  }
}))

// Toggle Button (Main Trigger)
export const ToggleButton = styled('button')({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  position: 'relative',
  margin: '8px',
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
  },
  
  '&:active': {
    transform: 'scale(0.95)',
  },
  
  '@media (max-width: 768px)': {
    width: '36px',
    height: '36px',
  }
})

// Sidebar Content
export const SidebarContent = styled('div')(({ isExpanded }) => ({
  opacity: isExpanded ? '1' : '0',
  transform: `translateX(${isExpanded ? '0' : '20px'})`,
  transition: 'all 0.3s ease 0.1s',
  padding: isExpanded ? '16px' : '0',
  pointerEvents: isExpanded ? 'auto' : 'none',
}))

// Action Items Container
export const ActionsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
})

// Individual Action Item
export const ActionItem = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.1)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  },
  
  '&:active': {
    transform: 'translateY(0)',
  }
})

// Action Icon Container
export const ActionIcon = styled('div')(({ type }) => {
  let gradient = 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
  
  switch(type) {
    case 'messages':
      gradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      break
    case 'profile':
      gradient = 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
      break
    case 'refresh':
      gradient = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
      break
  }
  
  return {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '16px',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
  }
})

// Action Text Content
export const ActionContent = styled('div')({
  flex: 1,
  minWidth: 0,
})

export const ActionTitle = styled('div')({
  color: 'white',
  fontWeight: 600,
  fontSize: '15px',
  marginBottom: '4px',
  lineHeight: 1.2,
})

export const ActionDescription = styled('div')({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '12px',
  lineHeight: 1.3,
})

// Notification Badge
export const NotificationBadge = styled('div')({
  position: 'absolute',
  top: '-6px',
  right: '-6px',
  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  color: 'white',
  borderRadius: '50%',
  minWidth: '20px',
  height: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '11px',
  fontWeight: 700,
  border: '2px solid rgba(30, 41, 59, 0.9)',
  boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
  zIndex: 10,
  animation: 'pulse 2s infinite',
  
  '@keyframes pulse': {
    '0%, 100%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.1)',
    }
  }
})

// Update Badge (for profile updates needed)
export const UpdateBadge = styled('div')({
  position: 'absolute',
  top: '-4px',
  right: '-4px',
  width: '12px',
  height: '12px',
  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  borderRadius: '50%',
  border: '2px solid rgba(30, 41, 59, 0.9)',
  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
  zIndex: 10,
  animation: 'bounce 2s infinite',
  
  '@keyframes bounce': {
    '0%, 20%, 53%, 80%, 100%': {
      transform: 'translateY(0)',
    },
    '40%, 43%': {
      transform: 'translateY(-8px)',
    },
    '70%': {
      transform: 'translateY(-4px)',
    },
    '90%': {
      transform: 'translateY(-2px)',
    }
  }
})

// Separator
export const ActionSeparator = styled('div')({
  height: '1px',
  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
  margin: '8px 0',
})

// Sidebar Header (when expanded)
export const SidebarHeader = styled('div')({
  paddingBottom: '16px',
  marginBottom: '16px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  textAlign: 'center',
})

export const SidebarTitle = styled('h3')({
  color: 'white',
  fontSize: '16px',
  fontWeight: 700,
  margin: '0 0 4px 0',
  background: 'linear-gradient(135deg, #fff 0%, #e5e7eb 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
})

export const SidebarSubtitle = styled('div')({
  color: 'rgba(255, 255, 255, 0.6)',
  fontSize: '12px',
})

// Close Button (for mobile)
export const CloseButton = styled('button')(({ isExpanded }) => ({
  position: 'absolute',
  top: '12px',
  right: '12px',
  background: 'rgba(255, 255, 255, 0.1)',
  border: 'none',
  borderRadius: '50%',
  width: '28px',
  height: '28px',
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'rgba(255, 255, 255, 0.7)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    transform: 'scale(1.1)',
  },
  
  '@media (max-width: 768px)': {
    display: isExpanded ? 'flex' : 'none',
  }
}))

// Backdrop for mobile
export const MobileBackdrop = styled('div')(({ isVisible }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(4px)',
  zIndex: 999,
  opacity: isVisible ? '1' : '0',
  visibility: isVisible ? 'visible' : 'hidden',
  transition: 'all 0.3s ease',
  
  '@media (min-width: 769px)': {
    display: 'none',
  }
}))

// Floating Labels (when collapsed)
export const FloatingLabel = styled('div')({
  position: 'absolute',
  right: '70px',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'rgba(0, 0, 0, 0.8)',
  color: 'white',
  padding: '8px 12px',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  opacity: 0,
  visibility: 'hidden',
  transition: 'all 0.3s ease',
  pointerEvents: 'none',
  zIndex: 1001,
  
  '&::after': {
    content: '""',
    position: 'absolute',
    right: '-4px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderLeft: '4px solid rgba(0, 0, 0, 0.8)',
    borderTop: '4px solid transparent',
    borderBottom: '4px solid transparent',
  }
})